/**
 * fs-reveal.js — Matevara scroll-reveal system.
 *
 * Mechanism:
 *   1. Adds `html.fs-reveal-ready` immediately so the CSS hides [data-fs-reveal]
 *      elements. Without this class everything stays visible (no-JS fallback).
 *   2. IntersectionObserver fires when ~10% of element enters viewport
 *      (threshold 0.1, rootMargin '0px 0px -5% 0px'). The small negative
 *      bottom margin means elements near the very bottom of the page still
 *      trigger — avoids the classic "bottom-section never fires" trap.
 *   3. On intersect: read data-fs-reveal-delay, write CSS variable
 *      --fs-reveal-delay, then add .fs-reveal-in. Unobserve immediately
 *      (one-shot reveal, no re-hide on scroll-up).
 *   4. Respects prefers-reduced-motion: when reduced-motion is set, skip
 *      marking html.fs-reveal-ready so the CSS never hides anything.
 *      Observer still runs but adds .fs-reveal-in instantly (no-op since CSS
 *      overrides anyway — belt-and-suspenders).
 *
 * Revert: remove assets/fs-reveal.css, assets/fs-reveal.js, the two lines in
 *         theme.liquid, and data-fs-reveal / data-fs-reveal-delay attributes
 *         from section files.
 */

(function () {
  'use strict';

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ─── Step 1: gate CSS hiding behind this class ─── */
  if (!reducedMotion) {
    document.documentElement.classList.add('fs-reveal-ready');
  }

  /* ─── Step 2: observe ─── */
  if (!('IntersectionObserver' in window)) {
    /* Fallback: no IO support — reveal everything immediately */
    var els = document.querySelectorAll('[data-fs-reveal]');
    for (var i = 0; i < els.length; i++) {
      els[i].classList.add('fs-reveal-in');
    }
    return;
  }

  var observer = new IntersectionObserver(
    function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;

        var el = entry.target;
        var delay = el.getAttribute('data-fs-reveal-delay');

        if (delay) {
          el.style.setProperty('--fs-reveal-delay', delay + 'ms');
        }

        /* Small rAF so the transition-delay var is applied before .fs-reveal-in
           triggers the transition. Without this, the first paint can skip the delay. */
        requestAnimationFrame(function () {
          el.classList.add('fs-reveal-in');
        });

        observer.unobserve(el); /* one-shot */
      });
    },
    {
      /*
        rootMargin bottom -100px: the trigger line sits 100px ABOVE the viewport
        bottom edge. An element fires only once its top has scrolled 100px into
        view — so reveals happen comfortably on-screen, not at the very edge.
        threshold 0 = fire as soon as it crosses that line. The scroll
        safetyReveal (vh-100) uses the same 100px line for jump/overshoot cases.
      */
      threshold: 0,
      rootMargin: '0px 0px -180px 0px'
    }
  );

  function observeAll() {
    var targets = document.querySelectorAll('[data-fs-reveal]:not(.fs-reveal-in)');
    targets.forEach(function (el) {
      observer.observe(el);
    });
  }

  /*
    Jump-safety net. IO fires reliably on gradual scroll, but an instant jump
    (anchor link, back/forward restore, fast mobile flick) can overshoot an
    element's trigger band — IO never reports it and it stays hidden forever.
    On every scroll-settle, reveal any element whose top is already above the
    viewport bottom (user has reached or passed it). rAF-throttled.
  */
  var ticking = false;
  function safetyReveal() {
    ticking = false;
    var vh = window.innerHeight || document.documentElement.clientHeight;
    var targets = document.querySelectorAll('[data-fs-reveal]:not(.fs-reveal-in)');
    targets.forEach(function (el) {
      if (el.getBoundingClientRect().top < vh - 180) {
        var delay = el.getAttribute('data-fs-reveal-delay');
        if (delay) el.style.setProperty('--fs-reveal-delay', delay + 'ms');
        el.classList.add('fs-reveal-in');
        observer.unobserve(el);
      }
    });
  }
  function onScroll() {
    if (!ticking) {
      ticking = true;
      requestAnimationFrame(safetyReveal);
    }
  }
  window.addEventListener('scroll', onScroll, { passive: true });

  /* Run on DOM ready */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', observeAll);
  } else {
    observeAll();
  }

  /* Shopify theme editor: re-observe after section reload */
  document.addEventListener('shopify:section:load', function () {
    observeAll();
  });
})();
